const noop = () => {};

const preloadedSources = new Set();

const canPreload = () =>
    typeof window !== "undefined" && typeof window.Image !== "undefined";

/**
 * @param {Function} callback
 * @returns {Function} cancels the scheduled callback
 */
const scheduleIdle = (callback) => {
    if (typeof window.requestIdleCallback === "function") {
        const handle = window.requestIdleCallback(callback, { timeout: 2000 });
        return () => window.cancelIdleCallback(handle);
    }

    const handle = window.setTimeout(callback, 200);
    return () => window.clearTimeout(handle);
};

/**
 * @param {string | Array<string | null | undefined>} sources
 * @returns {Array<string>} unique, non-empty sources
 */
export const normalizeImageSources = (sources) => [
    ...new Set(
        []
            .concat(sources ?? [])
            .filter((source) => typeof source === "string" && source !== ""),
    ),
];

const load = (source) =>
    new Promise((resolve) => {
        const image = new Image();

        image.onload = () => resolve();

        image.onerror = () => {
            preloadedSources.delete(source);
            resolve();
        };

        image.src = source;
    });

/**
 * @param {string | Array<string | null | undefined>} sources
 * @param {Object} [options]
 * @param {number} [options.concurrency=4] parallel requests
 * @param {boolean} [options.immediate=false] skip idle scheduling
 * @returns {Function} cancels queued work that has not started yet
 */
export const preloadImages = (sources, options = {}) => {
    const { concurrency = 4, immediate = false } = options;

    if (!canPreload()) {
        return noop;
    }

    const queue = normalizeImageSources(sources).filter(
        (source) => !preloadedSources.has(source),
    );

    if (queue.length === 0) {
        return noop;
    }

    queue.forEach((source) => preloadedSources.add(source));

    let cancelled = false;

    const drain = () => {
        const source = queue.shift();

        if (cancelled || source === undefined) {
            return Promise.resolve();
        }

        return load(source).then(drain);
    };

    const start = () =>
        Promise.all(
            Array.from({ length: Math.min(concurrency, queue.length) }, drain),
        );

    let cancelSchedule = noop;

    if (immediate) {
        start();
    } else {
        cancelSchedule = scheduleIdle(start);
    }

    return () => {
        cancelled = true;
        cancelSchedule();
        queue.forEach((source) => preloadedSources.delete(source));
    };
};

export default preloadImages;
